
import { mapActions, mapGetters } from 'vuex';
import UICreatePostModal from '@/components/ui/organism/UICreatePostModal';
import UICreatePostMobileModal from '@/components/ui/organism/UICreatePostMobileModal';
import UICreateReplyModal from '@/components/ui/organism/UICreateReplyModal';
import {
  uploadPostImage,
  searchMentions,
  searchHashtags,
} from '@/utils/mixins';
import { showToast, showErrorToast } from '~/utils/toast-notifications';
import { isMobile } from '~/utils/screenSizes';
import { appURLs } from '@/utils/route-redirects';
import {
  getHashtagString,
  getProfileMentionString,
} from '~/utils/post-mentions';
import { eventManager } from '~/utils/behavior/applicationEvents/event-manager';

export default {
  name: 'CreatePostModalWrapper',
  components: {
    UICreatePostModal,
    UICreateReplyModal,
    UICreatePostMobileModal,
  },
  mixins: [uploadPostImage, searchMentions, searchHashtags],
  data() {
    return {
      clear: false,
    };
  },
  computed: {
    ...mapGetters('profile', ['profile']),
    ...mapGetters('authentication', ['profile', 'isEmailConfirmed']),
    ...mapGetters('modals', ['showCreatePostModalRes']),
    ...mapGetters('api', ['createPostRes']),
    ...mapGetters({
      updatedPost: 'post/post',
    }),
    ...mapGetters('post', ['replies']),
    avatarUri() {
      if (this.profile && this.profile.avatarUri) {
        return this.profile.avatarUri;
      }
      return '';
    },
    displayName() {
      if (this.profile && this.profile.displayName) {
        return this.profile.displayName;
      }
      return '';
    },
    isMobileSize() {
      return isMobile();
    },
    filteredTransactionData() {
      const data = this.showCreatePostModalRes?.transactionData;
      if (!data) return null;

      const {
        companyUri,
        companyName,
        symbolName,
        operationType,
        changePercent,
        portfolioId,
        companyId,
        companyProfileId,
        transactionType,
      } = data;

      return {
        companyUri,
        companyName,
        symbolName,
        operationType,
        price: data.price ?? data.itemPrice,
        currency: data.currency ?? data.chosenCurrency,
        quantity: data.quantity ?? data.itemQuantity,
        // TODO for Nikola: BE expects non-nullable decimal, so I am currently sending 0 for Buy.
        // Null is more precise value so consider making changePercent on BE side nullable
        changePercent: operationType === 'Sell' ? changePercent : 0,
        portfolioId,
        companyId: companyId ?? companyProfileId,
        transactionType: transactionType,
      };
    },
  },
  methods: {
    ...mapActions('api', ['createPost', 'updatePost']),
    ...mapActions('modals', ['showCreatePostModal']),
    ...mapActions('post', { postAppendNewNestedReply: 'appendNewNestedReply' }),
    ...mapActions('home', {
      homeAppendNestedReplyTop: 'appendNestedReplyTop',
      homeAppendNewsNestedReplyTop: 'appendNewsNestedReplyTop',
    }),
    ...mapActions('profile', ['getProfileById']),
    ...mapActions('hashtags', {
      hashtagsAppendNestedReplyTop: 'appendNestedReplyTop',
    }),
    ...mapActions('cryptoNews', {
      cryptoAppendNestedReplyTop: 'appendNestedReplyTop',
    }),
    ...mapActions('market', {
      marketAppendNestedReplyTop: 'appendNestedReplyTop',
    }),
    ...mapActions('profile', {
      profileAppendNestedReplyTop: 'appendNestedReplyTop',
    }),
    ...mapActions('leagues', {
      leagueAppendNestedReplyTop: 'appendNestedReplyTop',
      leagueAppendNewReplyTop: 'appendNewReplyTop',
    }),
    ...mapActions('portfolio', {
      appendNewPortfolioNestedReply: 'appendNewNestedReply',
      appendNewReply: 'appendNewReply',
    }),
    ...mapActions({
      appendToHomeFeed: 'home/appendNewPostAtTop',
      appendToProfileFeed: 'profile/appendNewPostAtTop',
    }),
    ...mapActions('post', ['getPost', 'getReplies']),
    ...mapActions({
      updatePostOnHomeFeed: 'home/updatePostAfterEdit',
      updateReplyOnHomeFeed: 'home/updateReply',
      updatePostOnProfileFeed: 'profile/updatePostAfterEdit',
      updateReplyOnProfileFeed: 'profile/updateReply',
      updatePostOnPostPage: 'post/updatePostAfterEdit',
      updateReplyOnPostPage: 'post/updateReply',
      updatePostOnMarketPage: 'market/updatePostAfterEdit',
      updateReplyOnMarketPage: 'market/updateReply',
      updateReplyOnPortfolioPage: 'portfolio/updateReply',
      updateReplyOnCompanyPage: 'companyProfile/updateReply',
      updateNestedReplyOnCompanyPage: 'companyProfile/updateNestedReply',
      updatePostOnLeagueFeed: 'leagues/updatePostAfterEdit',
      updateReplyOnLeagueFeed: 'leagues/updateReply',
    }),
    ...mapActions('leagues', ['appendNewPostAtTop']),
    onSubmit(data) {
      this.createPost(data)
        .then((res) => {
          const hashtagString = getHashtagString(res.data.hashtags);
          const profileMentionString = getProfileMentionString(
            res.data.mentions
          );
          eventManager.raiseCreatePostEvent({
            post_name: '',
            tag_list: hashtagString,
            link_list: profileMentionString,
          });
          if (this.showCreatePostModalRes.context) {
            if (this.showCreatePostModalRes.context.page == 'postDetail') {
              if (
                this.showCreatePostModalRes.context.action ==
                'createNestedReply'
              ) {
                this.postAppendNewNestedReply({
                  postId: this.showCreatePostModalRes.context.params.postId,
                  post: res.data,
                });
              }
            }
            if (
              this.showCreatePostModalRes.context.page == 'portfolioComments'
            ) {
              if (
                this.showCreatePostModalRes.context.action ==
                'appendNewPortfolioNestedReply'
              ) {
                this.appendNewPortfolioNestedReply({
                  postId: this.showCreatePostModalRes.context.params.postId,
                  post: res.data,
                });
              }
            }
            if (
              this.showCreatePostModalRes.context.page == 'portfolioDetails'
            ) {
              if (
                this.showCreatePostModalRes.context.action == 'appendNewReply'
              ) {
                this.appendNewReply(res.data);
              }
            }
            if (this.showCreatePostModalRes.context.page == 'home') {
              if (
                this.showCreatePostModalRes.context.action ==
                'homeAppendNestedReplyTop'
              ) {
                this.homeAppendNestedReplyTop({
                  data: res.data,
                  postId: this.showCreatePostModalRes.context.params.postId,
                });
              }
              if (
                this.showCreatePostModalRes.context.action ==
                'homeAppendNewsNestedReplyTop'
              ) {
                this.homeAppendNewsNestedReplyTop({
                  data: res.data,
                  postId: this.showCreatePostModalRes.context.params.postId,
                });
              }
            }
            if (this.showCreatePostModalRes.context.page == 'crypto') {
              if (
                this.showCreatePostModalRes.context.action ==
                'cryptoAppendNestedReplyTop'
              ) {
                this.cryptoAppendNestedReplyTop({
                  data: res.data,
                  postId: this.showCreatePostModalRes.context.params.postId,
                });
              }
            }
            if (this.showCreatePostModalRes.context.page == 'market') {
              if (
                this.showCreatePostModalRes.context.action ==
                'marketAppendNestedReplyTop'
              ) {
                this.marketAppendNestedReplyTop({
                  data: res.data,
                  postId: this.showCreatePostModalRes.context.params.postId,
                });
              }
            }
            if (this.showCreatePostModalRes.context.page == 'profile') {
              if (
                this.showCreatePostModalRes.context.action ==
                'profileAppendNestedReplyTop'
              ) {
                this.profileAppendNestedReplyTop({
                  data: res.data,
                  postId: this.showCreatePostModalRes.context.params.postId,
                });
              }
            }
            if (this.showCreatePostModalRes.context.page == 'league') {
              if (
                this.showCreatePostModalRes.context.action ==
                'leagueAppendNestedReplyTop'
              ) {
                this.leagueAppendNestedReplyTop({
                  data: res.data,
                  postId: this.showCreatePostModalRes.context.params.postId,
                });
              } else if (
                this.showCreatePostModalRes.context.action ==
                'appendNewReplyTop'
              ) {
                this.leagueAppendNewReplyTop(res.data);
              }
            }
            if (this.showCreatePostModalRes.context.page == 'hashtags') {
              if (
                this.showCreatePostModalRes.context.action ==
                'hashtagsAppendNestedReplyTop'
              ) {
                this.hashtagsAppendNestedReplyTop({
                  data: res.data,
                  postId: this.showCreatePostModalRes.context.params.postId,
                });
              }
            }
          }
          // TODO from Nikola: This logic should be refactored to be more clear and easier to understand
          // Reaching this point, we know we are in a global context
          // Could be coming from the CreatePostButton available in multiple places, or from a reply/repost, which can also be done from multiple places
          // Reposts are always flagged with a Profile targetType (including when coming from a league page)
          // Regular posts from the CreatePost button also have a Profile targetType, except when used from a league page (in which case, it has LEAGUE)
          // Replies have a POST targetType
          if (res.data.targetType == 'PROFILE') {
            // This is a repost from a league page
            // Only append at the top of the league feed
            // TODO from Julien: unclear as to why reposts have to be targetting a profile. Could use some investigation if we ever decide to refactor/clarify post logic.
            // From testing alone, making a repost with a LEAGUE targetType doesn't seem to cause functionnal issues, so worth investigating
            // and eventually change the targetType in LeagueFeed.vue when opening the modal for a repost
            if (this.showCreatePostModalRes.league) {
              this.appendNewPostAtTop(res.data);
            } else {
              // This is either a regular post from the CreatePost button, or a repost
              // Only append at the top of the profile feed if we're on the user's own profile page (profile feed gets refreshed when landing on the profile, so no need to do this otherwise)
              this.$route.name?.includes(appURLs.profile()) &&
                this.$route.path.includes(this.profile.profileId) &&
                this.appendToProfileFeed(res.data);
              // Append to main feed in all cases as it's not always refreshed when landing on the homepage
              this.appendToHomeFeed(res.data);
            }
          }

          // This case is regular posts created from the mobile floating CreatePost button from a league page
          if (res.data.targetType == 'LEAGUE') {
            this.appendNewPostAtTop(res.data);
          }
          showToast(this.$t('post_created_ok'));

          this.showCreatePostModalRes.dispatch &&
            this.$store.dispatch(this.showCreatePostModalRes.dispatch);
          this.showCreatePostModal();

          this.clear = true;
          setTimeout(() => {
            this.clear = false;
          }, 100);
        })
        .catch((error) => {
          console.error(error);
          showErrorToast(this.$t('error_occured_message'));
        });
    },
    onUpdate(data) {
      this.updatePost(data)
        .then(async (res) => {
          this.handlePostUpdate(res.data, data);
        })
        .catch(() => this.$notify.error(this.$t('error_occured_message')));
    },
    async handlePostUpdate(postObject, data) {
      await this.getPost(data.postId);

      if (postObject) {
        const isProfileRoute = this.$route.fullPath.includes(appURLs.profile());
        const isHomeRoute = this.$route.name?.includes('index');
        const isPostRoute = this.$route.name?.includes('post');
        const isMarketRoute = this.$route.name?.includes('market');
        const isPortfolioRoute = this.$route.name?.includes('portfolios');
        const isLeagueRoute = this.$route.name?.includes('leagues-leagueId');

        if (this.updatedPost) {
          this.updatedPost.replies = data.replies ?? [];
          this.updatedPost.replyCount = data.replyCount ?? 0;

          if (isPortfolioRoute) {
            await this.getReplies({
              postId: data.postId,
              params: { limit: 10, orderBy: 'CreatedDateDescending' },
            });
            this.updateReplyOnPortfolioPage({
              postId: data.postId,
              data: this.updatedPost,
            });
          } else if (isMarketRoute) {
            await this.getReplies({
              postId: data.postId,
              params: { limit: 10, orderBy: 'CreatedDateDescending' },
            });
            this.updateReplyOnMarketPage({
              postId: data.postId,
              data: this.updatedPost,
            });
          } else if (data.targetType === 'LEAGUE') {
            // TODO from Nikola: route checks here are done before await
            // which could lead to race condition issues
            if (isLeagueRoute) {
              this.updatePostOnLeagueFeed(this.updatedPost);
            }
          } else if (data.targetType === 'PROFILE') {
            if (isProfileRoute) {
              this.updatePostOnProfileFeed(this.updatedPost);
            } else if (isHomeRoute) {
              this.updatePostOnHomeFeed(this.updatedPost);
            } else if (isPostRoute) {
              this.updatePostOnPostPage(this.updatedPost);
            }
          } else if (data.targetType === 'COMPANY') {
            this.updatedPost.target = data.postId;
            this.updateReplyOnCompanyPage({
              postId: data.target,
              data: this.updatedPost,
            });
          } else if (data.targetType === 'POST') {
            if (isLeagueRoute) {
              this.updateReplyOnLeagueFeed({
                postId: data.target,
                data: this.updatedPost,
              });
            } else if (isProfileRoute) {
              if (this.profile.profileId !== this.$route.params.profileId) {
                this.updateNestedReplyOnCompanyPage({
                  postId: data.postId,
                  data: this.updatedPost,
                });
              } else {
                this.updatedPost.target = data.postId;
                this.updateReplyOnProfileFeed({
                  postId: data.target,
                  data: this.updatedPost,
                });
              }
            } else if (isHomeRoute) {
              this.updatedPost.target = data.postId;
              this.updateReplyOnHomeFeed({
                postId: data.target,
                data: this.updatedPost,
              });
            } else if (isPostRoute) {
              await this.getReplies({
                postId: data.postId,
                params: { limit: 10, orderBy: 'CreatedDateDescending' },
              });
              this.updateReplyOnPostPage({
                postId: data.postId,
                data: this.updatedPost,
              });
            }
          }

          this.showCreatePostModal();
        }
      }
    },
  },
};
